import { useEffect, useState, useRef, useCallback } from 'react';
import { dayjs, Dayjs } from 'lib';
import { RadarView, ScalingTendenceItem } from 'queries/scalings/contract';
import { TData } from 'types';

type Domain = {
  name: string;
  domain: [number, number];
  id?: number;
};

type RadarPoint = {
  domain: string;
  value: number;
  x: number;
  y: number;
  dataName: string | undefined;
};

type ReactEChartsRadarOptions = {
  tooltip: object;
  radar: {
    name: {
      textStyle: {
        fontSize: number;
      };
    };
    indicator: { name: string; max: number }[];
    radius: string;
  };
  series: {
    type: string;
    data: {
      value: number[];
      symbolSize: number;
      lineStyle: {
        type: string;
        width: number;
        color: string;
      };
      areaStyle: {
        opacity: number;
        color: string;
      };
    }[];
  }[];
};

const MAX_SLIDER_ITEMS = 180;

interface AbstractSeriesPoint {
  [key: string]: any;
}

interface Props {
  initialValue?: Dayjs;
  data?: RadarView;
}

const optionsDefault: ReactEChartsRadarOptions = {
  tooltip: {},
  radar: {
    name: {
      textStyle: {
        fontSize: 8,
      },
    },
    indicator: [],
    radius: '70%',
  },
  series: [
    {
      type: 'radar',
      data: [
        {
          value: [],
          symbolSize: 1,
          lineStyle: {
            type: 'solid',
            width: 1,
            color: '#FF5454',
          },
          areaStyle: {
            opacity: 0.4,
            color: '#FF5454',
          },
        },
      ],
    },
  ],
};

const useRadarViewChart = ({ data, initialValue }: Props) => {
  const [scalingDates, setScalingDates] = useState<string[]>([]);
  const [dataPoints, setDataPoints] = useState<AbstractSeriesPoint[] | undefined>(undefined);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [options, setOptions] = useState<ReactEChartsRadarOptions>(optionsDefault);
  const [hoverCell, setHoverCell] = useState<RadarPoint | undefined>(undefined);
  const [scalingDomain, setScalingDomain] = useState<Domain[]>([]);
  const [sliderItemsQty, setSliderItemsQty] = useState<number>(MAX_SLIDER_ITEMS);
  const [isPlayingAnimation, setIsPlayingAnimation] = useState<boolean>(false);

  const countRef = useRef(sliderValue);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  countRef.current = sliderValue;

  const handleChange = useCallback((_: Event, value: number | number[]) => {
    if (typeof value === 'number') setSliderValue(value);
  }, []);

  const handleValueLabelFormat = (value: number) => dayjs(scalingDates[value]).format('DD/MM/YYYY');

  const playPauseAnimation = useCallback(() => {
    const updater = (): void => {
      if (countRef.current < sliderItemsQty) {
        setSliderValue((stateSliderValue) => countRef.current + 1);
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setIsPlayingAnimation(false);
      }
    };
    const status = !isPlayingAnimation;
    setIsPlayingAnimation(status);
    if (status) {
      intervalRef.current = setInterval(updater, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isPlayingAnimation, sliderItemsQty]);

  const stopAnimation = useCallback(() => {
    setIsPlayingAnimation(false);
    playPauseAnimation();
    setSliderValue(0);
  }, [isPlayingAnimation]);

  const parseData = (scalingDataItens: ScalingTendenceItem[], date: string) => {
    const models = scalingDataItens.filter((item) => item.timestamp === date)[0]?.models;
    const domain: Domain[] = [];
    optionsDefault.radar.indicator = [];
    optionsDefault.series[0].data[0].value = [];
    const seriesData: AbstractSeriesPoint = {};
    let statusColor = '#33CC99';
    if (models) {
      models.forEach((item, i: number) => {
        if (item.prediction_score > 0.75) {
          statusColor = '#FF5454';
        } else if (item.prediction_score > 0.5 && statusColor !== '#FF5454') {
          statusColor = '#FFB054';
        }
        const name = item.model_name.replace(/_/g, ' ').slice(4);
        seriesData[name] = item.prediction_score;
        optionsDefault.radar.indicator.push({ name, max: 1 });
        optionsDefault.series[0].data[0].value.push(item.prediction_score);
        setOptions(optionsDefault);
        domain.push({
          id: item.model_id,
          name,
          domain: [0, 1],
        });
      });
    }

    let x = 1;
    const fakeAxes = [];

    while (x > 0) {
      let position = x;
      const d: TData = { stroke: '#CBCBCB', fill: '#FFF', name: 'border' };

      Object.keys(seriesData).forEach((item) => {
        d[item] = position;
      });

      x = Math.round((x - 0.1) * 100) / 100;
      position = Math.round((position - 0.1) * 100) / 100;
      fakeAxes.push(d);
    }

    seriesData.fill = statusColor;
    seriesData.stroke = statusColor;
    fakeAxes.push(seriesData);
    setScalingDomain(domain);
    setDataPoints(fakeAxes);
  };

  const getSliderValueIndex = (dates: string[]): number => {
    let dateRangeIndex = 0;

    if (initialValue) {
      const initialValueIndex = dates.findIndex(
        (item) => item === initialValue.format('YYYY-MM-DD'),
      );

      if (initialValueIndex !== -1) dateRangeIndex = initialValueIndex;
      else {
        for (let i = 0; i < dates.length; i += 1) {
          if (dayjs(dates[i], 'YYYY-MM-DD').isSameOrBefore(initialValue, 'day')) dateRangeIndex = i;
          else break;
        }
      }
    }

    return dateRangeIndex;
  };

  useEffect(() => {
    if (isPlayingAnimation) playPauseAnimation();

    if (data && data.length > 0) {
      const dates = data.map(({ timestamp }) => timestamp).sort();
      setSliderItemsQty(dates.length - 1);
      setSliderValue(getSliderValueIndex(dates));
      setScalingDates(dates);
    }
  }, [data, options]);

  useEffect(() => {
    if (data && scalingDates.length > 0) parseData(data, scalingDates[sliderValue]);
  }, [sliderValue, scalingDates, options]);

  return {
    dataPoints,
    scalingDomain,
    hoverCell,
    isPlayingAnimation,
    sliderItemsQty,
    sliderValue,
    options,
    handleChange,
    setHoverCell,
    handleValueLabelFormat,
    playPauseAnimation,
    stopAnimation,
  };
};

export default useRadarViewChart;
